import { PayloadAction } from '@reduxjs/toolkit'
import {
  call,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects'

import history from '@/history'
import i18n from '@/i18n'
import { userRoles } from '@/redux/auth/selectors'
import { messageHandlerSet } from '@/redux/messageHandler/actions'
import * as ScheduledExercisesAPI from '@/services/scheduledExercise'
import * as ScheduledExerciseSettingsAPI from '@/services/scheduledExerciseSettings'
import * as ScheduleNotesAPI from '@/services/scheduleNotes'
import * as SchedulesAPI from '@/services/schedules'
import {
  IAssignScheduleResponse,
  UserRole
} from '@/types'
import { ScheduleExerciseModel } from '../exercises/types'
import {
  addMultipleScheduledExercisesFailed,
  addMultipleScheduledExercisesRequest,
  addMultipleScheduledExercisesSuccess,
  assignScheduleFailed,
  assignScheduleRequest,
  assignScheduleSuccess,
  deleteScheduledExercisesByIdsFailed,
  deleteScheduledExercisesByIdsRequest,
  deleteScheduledExercisesByIdsSuccess,
  deleteScheduleFailed,
  deleteScheduleRequest,
  deleteScheduleSuccess,
  editScheduledExercisesByIdsFailed,
  editScheduledExercisesByIdsRequest,
  editScheduledExercisesByIdsSuccess,
  editScheduledExerciseSettingsByIdsFailed,
  editScheduledExerciseSettingsByIdsRequest,
  editScheduledExerciseSettingsByIdsSuccess,
  editScheduleFailed,
  editScheduleRequest,
  editScheduleSuccess,
  getSchedulesFailed,
  getSchedulesRequest,
  getSchedulesSuccess,
  getSingleScheduleFailed,
  getSingleScheduleRequest,
  getSingleScheduleSuccess
} from './actions'
import type {
  AddMultipleScheduledExercisesRequestPayload,
  AssignScheduleRequestPayload,
  DeleteScheduledExercisesByIdsRequestPayload,
  DeleteScheduleRequestPayload,
  EditScheduledExercisesByIdsRequestPayload,
  EditScheduledExerciseSettingsByIdsRequestPayload,
  EditScheduleRequestPayload,
  GetSchedulesRequestPayload,
  GetSingleScheduleRequestPayload,
  SchedulesModel
} from './types'

function * getSchedulesSaga ({
  payload
}: PayloadAction<GetSchedulesRequestPayload | undefined>) {
  const roles: string = yield select(userRoles)

  try {
    let schedulesData: SchedulesModel[] = []

    if (roles.includes('patient')) {
      schedulesData = yield call(SchedulesAPI.getMySchedules)
    }

    if (roles.includes('doctor') && payload?.patientId) {
      schedulesData = yield call(SchedulesAPI.getSchedulesByPatient, {
        patientId: payload.patientId
      })
    }

    if (schedulesData) {
      yield put(getSchedulesSuccess({ schedulesData }))
    } else {
      yield put(getSchedulesFailed())
    }
  } catch (err) {
    yield put(getSchedulesFailed())
  }
}

function * assignScheduleSaga ({
  payload
}: PayloadAction<AssignScheduleRequestPayload>) {
  const { patientId } = payload
  const { scheduleNotes, callbackAction, ...requestBody } = payload

  try {
    const scheduleData: IAssignScheduleResponse = yield call(
      SchedulesAPI.assignSchedule,
      requestBody
    )

    const scheduleId = scheduleData.data.id

    if (!scheduleId) throw Error

    if (scheduleNotes && scheduleNotes.length > 0) {
      yield call(ScheduleNotesAPI.addBatchScheduleNote, {
        scheduleId,
        notes: scheduleNotes.map(({ text }, index) => ({
          text,
          order: index
        }))
      })
    }

    if (callbackAction) {
      yield put(callbackAction)
    }

    if (scheduleData) {
      yield put(
        messageHandlerSet({
          description: i18n.t('gui.notification.schedule_assigned'),
          type: 'success'
        })
      )

      history.replace(`/patients/${patientId}`)

      yield put(assignScheduleSuccess())
    } else {
      yield put(assignScheduleFailed())
    }
  } catch (err) {
    yield put(assignScheduleFailed())
  }
}

function * getSingleScheduleSaga ({
  payload
}: PayloadAction<GetSingleScheduleRequestPayload>) {
  const { id } = payload

  const roles: UserRole[] = yield select(userRoles)

  try {
    const scheduleData: SchedulesModel = yield call(
      SchedulesAPI.getSingleSchedule,
      { id, roles }
    )

    if (scheduleData) {
      yield put(getSingleScheduleSuccess(scheduleData))
    } else {
      yield put(getSingleScheduleFailed())
    }
  } catch (err) {
    yield put(getSingleScheduleFailed())
  }
}

function * editScheduleSaga ({
  payload
}: PayloadAction<EditScheduleRequestPayload>) {
  const { id, requestPayload } = payload

  try {
    const scheduleData: SchedulesModel = yield call(SchedulesAPI.editSchedule, {
      id,
      requestPayload
    })

    yield put(editScheduleSuccess(scheduleData))
  } catch (err) {
    yield put(editScheduleFailed())
  }
}

function * deleteScheduleSaga ({
  payload
}: PayloadAction<DeleteScheduleRequestPayload>) {
  const { id } = payload

  try {
    yield call(SchedulesAPI.deleteSchedule, { id })
    yield put(deleteScheduleSuccess({ id }))
  } catch (err) {
    yield put(deleteScheduleFailed())
  }
}

function * editScheduledExerciseSettingsByIdsSaga ({
  payload
}: PayloadAction<EditScheduledExerciseSettingsByIdsRequestPayload>) {
  try {
    const { exercisesIds, value, textId } = payload

    yield call(ScheduledExerciseSettingsAPI.editBatchScheduledExerciseSetting, {
      editScheduledExerciseSettingRequestPayload: {
        settings: exercisesIds.map(exerciseId => ({
          scheduledExerciseId: exerciseId,
          settingTextId: textId,
          newValue: value
        }))
      }
    })

    yield put(editScheduledExerciseSettingsByIdsSuccess(payload))
  } catch (err) {
    console.error(err)
    yield put(editScheduledExerciseSettingsByIdsFailed())
  }
}

function * deleteScheduledExercisesByIdsSaga ({
  payload
}: PayloadAction<DeleteScheduledExercisesByIdsRequestPayload>) {
  const { scheduleId, scheduledExerciseIds } = payload
  try {
    yield call(ScheduledExercisesAPI.deleteBatchScheduledExercise, {
      scheduledExerciseIds
    })

    yield put(
      deleteScheduledExercisesByIdsSuccess({
        scheduleId,
        scheduledExerciseIds
      })
    )
  } catch (err) {
    console.error(err)
    yield put(deleteScheduledExercisesByIdsFailed())
  }
}

function * editScheduledExercisesByIdsSaga ({
  payload
}: PayloadAction<EditScheduledExercisesByIdsRequestPayload>) {
  const { scheduledExercisesData } = payload

  try {
    yield call(ScheduledExercisesAPI.editBatchScheduledExercise, {
      scheduledExercisesData
    })

    yield put(editScheduledExercisesByIdsSuccess(payload))
  } catch (err) {
    console.error(err)
    yield put(editScheduledExercisesByIdsFailed())
  }
}

function * addMultipleScheduledExercisesSaga ({
  payload
}: PayloadAction<AddMultipleScheduledExercisesRequestPayload>) {
  try {
    const { scheduledExercisesData, scheduleId, callbackAction } = payload

    const response: ScheduleExerciseModel[] = yield call(
      ScheduledExercisesAPI.addBatchScheduledExercise,
      {
        addScheduledExerciseRequestPayload: {
          exercises: scheduledExercisesData,
          scheduleId
        }
      }
    )

    if (callbackAction) {
      yield put(callbackAction)
    }

    yield put(
      addMultipleScheduledExercisesSuccess({
        scheduledExercises: response,
        scheduleId
      })
    )
  } catch (err) {
    console.error(err)
    yield put(addMultipleScheduledExercisesFailed())
  }
}

function * schedulesSaga () {
  yield takeLatest(getSchedulesRequest.type, getSchedulesSaga)
  yield takeLatest(assignScheduleRequest.type, assignScheduleSaga)
  yield takeEvery(getSingleScheduleRequest.type, getSingleScheduleSaga)
  yield takeEvery(editScheduleRequest.type, editScheduleSaga)
  yield takeEvery(deleteScheduleRequest.type, deleteScheduleSaga)
  yield takeLatest(
    editScheduledExerciseSettingsByIdsRequest.type,
    editScheduledExerciseSettingsByIdsSaga
  )
  yield takeEvery(
    deleteScheduledExercisesByIdsRequest.type,
    deleteScheduledExercisesByIdsSaga
  )
  yield takeEvery(
    editScheduledExercisesByIdsRequest.type,
    editScheduledExercisesByIdsSaga
  )
  yield takeLatest(
    addMultipleScheduledExercisesRequest.type,
    addMultipleScheduledExercisesSaga
  )
}

export default schedulesSaga
